import Box from '@amzn/meridian/box';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Profile } from '../../Types';
import Text from '@amzn/meridian/text';
import ProfileContainer from '../Dashboard/DashboardContainers/ProfileContainer';
import { TRAILING_DAYS } from '../Dashboard/DashboardComponents/LighthouseMetrics/types';
import { weeksList } from '../../Util/Week';
import {
  TrainingRecord,
  TrainingTableSortColumn,
  ViewContext,
} from './TrainingList/types';
import useInspireOptions from '../../hooks/useInspireOptions';
import Column from '@amzn/meridian/column/column';
import TrainingTable from './TrainingList/TrainingTable';
import { SortDirection } from '../../Types/SortDirection';
import { sortTrainings } from './TrainingList/utils';
import Alert from '@amzn/meridian/alert';
import { format, parseISO } from 'date-fns';
import { TrainingViewMode } from './types/constants';
import useAssignedTraining from './hooks/useAssignedTraining';

interface TrainingCsaHomeProps {
  profile: Profile;
  profileState: string;
}

const TrainingCsaHome: React.FC<TrainingCsaHomeProps> = ({
  profile,
  profileState,
}) => {
  const setWeek = (e: React.FormEvent<HTMLSelectElement>): void => {
    setWeekValue(e.toString());
  };

  const { cscEntityPrefix } = useInspireOptions();
  const weekOptions = [TRAILING_DAYS, ...weeksList()];
  const [weekValue, setWeekValue] = useState(weekOptions[0]);
  const [sortColumn, setSortColumn] = useState<TrainingTableSortColumn>(
    TrainingTableSortColumn.DUE_DATE
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.DESCENDING
  );

  const { loading, error, trainings } = useAssignedTraining(
    profile,
    cscEntityPrefix,
    TrainingViewMode.CSA
  );

  const lastUpdatedTraining: string | undefined = useMemo(() => {
    if (!trainings) return undefined;
    let lastUpdatedTraining: string | undefined = undefined;

    trainings.forEach((training: TrainingRecord) => {
      // Greedy - take the first latestTranscriptTimestamp whether it is a string or undefined
      if (!lastUpdatedTraining) {
        lastUpdatedTraining = training?.latestTranscriptTimestamp;
        return;
      }

      // If we have some non undefined value for lastUpdatedTraining, skip any undefined trainings
      if (!training?.latestTranscriptTimestamp) {
        return;
      }

      // If we get here, both lastUpdatedTraining and training.latestTranscriptTimestamp are not undefined. Evaluate timestamps.
      lastUpdatedTraining =
        training.latestTranscriptTimestamp < lastUpdatedTraining
          ? training.latestTranscriptTimestamp
          : lastUpdatedTraining;
    });

    return lastUpdatedTraining;
  }, [trainings]);

  // Filter and sort trainings based on current state
  const filteredAndSortedTrainings = useMemo(() => {
    let filteredTrainings = trainings;

    filteredTrainings = sortTrainings(
      filteredTrainings,
      sortColumn,
      sortDirection
    );

    return filteredTrainings;
  }, [trainings, sortColumn, sortDirection]);

  const handleSort = (
    column: TrainingTableSortColumn,
    direction: SortDirection
  ) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  return (
    <>
      <Box height='100%'>
        <ProfileContainer
          state={profileState}
          profile={profile}
          selectedWeek={weekValue}
          setWeek={setWeek}
          isBig={true}
        />
        <Box spacingInset='450 none 450'>
          <Text type='h500'>
            <FormattedMessage
              id='cs_perfportal_training_page_assignments_title_csa'
              defaultMessage='My Trainings'
            />
          </Text>
        </Box>
        <Box spacingInset='none none 500'>
          <Alert type='warning' size='large'>
            <Text>
              <FormattedMessage
                id='cs_perfportal_trainings_csa_disclaimer'
                defaultMessage='<b>Note</b>: Updates to training information can take several hours to reflect.'
                values={{
                  b: chunks => <strong>{chunks}</strong>,
                }}
              />{' '}
              {lastUpdatedTraining ? (
                <FormattedMessage
                  id='cs_perfportal_training_last_updated'
                  defaultMessage='Last updated: {lastUpdated}'
                  values={{
                    lastUpdated: format(
                      parseISO(lastUpdatedTraining),
                      'MMMM d, yyyy, h:mm a'
                    ),
                  }}
                />
              ) : (
                <FormattedMessage
                  id='cs_perfportal_no_trainings_last_update'
                  defaultMessage='Unable to identify when the trainings were last updated.'
                />
              )}
            </Text>
          </Alert>
        </Box>
        <Column width='100%' alignmentVertical='top'>
          <Box type='outline' width='100%' spacingInset='200'>
            <Column alignmentVertical='top'>
              {/* Table Section */}
              <Box spacingInset='300'>
                <TrainingTable
                  trainings={filteredAndSortedTrainings}
                  loading={loading}
                  error={error}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  viewContext={ViewContext.CSA}
                  onSort={handleSort}
                  isCSAExperience={true}
                />
              </Box>
            </Column>
          </Box>
        </Column>
      </Box>
    </>
  );
};

export default TrainingCsaHome;
